import { User } from 'firebase/auth';

export interface AuthCheckResult {
    canProceed: boolean;
    requiresAuth: boolean;
    reason?: 'save' | 'read-limit'; 
}

/**
 * Check if user can save a story
 */
export const canSaveStory = (currentUser: User | null): AuthCheckResult => {
    if (currentUser) {
        return { canProceed: true, requiresAuth: false };
    }
    
    return {
        canProceed: false,
        requiresAuth: true,
        reason: 'save'
    };
};

/**
 * Check if user can continue reading stories
 */
export const canReadStory = (currentUser: User | null, storiesRead: number, maxReads: number): AuthCheckResult => {
    if (currentUser) {
        return { canProceed: true, requiresAuth: false };
    }
    
    if (storiesRead >= maxReads) {
        return {
            canProceed: false,
            requiresAuth: true,
            reason: 'read-limit'
        };
    }
    
    return { canProceed: true, requiresAuth: false };
};

/**
 * Check if user can access dashboard features
 */
export const canAccessDashboard = (currentUser: User | null): AuthCheckResult => {
    if (currentUser) {
        return { canProceed: true, requiresAuth: false };
    }
    
    return {
        canProceed: false,
        requiresAuth: true
    };
};

// ============================================
// ROLE UTILITIES (NEW)
// ============================================

const APPROVED_TEACHER_DOMAINS = [
  '.edu',
  '.k12.us',
  '.k12.ca.us',
  '.k12.tx.us',
  '.k12.il.us',
  '.k12.ny.us',
  '.k12.fl.us',
  '.sch.uk',
  '.ac.uk',
  '.edu.au',
  '.edu.ca'
];

export type UserRole = 'student' | 'teacher' | 'pending_teacher';

/**
 * Determine user role based on email domain
 */
export const determineRoleFromEmail = (email: string): UserRole => {
  const emailLower = email.toLowerCase();
  
  // Check if email ends with approved educational domain
  const isEducationalEmail = APPROVED_TEACHER_DOMAINS.some(domain => 
    emailLower.endsWith(domain)
  );
  
  if (isEducationalEmail) {
    return 'teacher';
  }
  
  // Default to student for all other emails
  return 'student';
};

/**
 * Check if an email is from an approved educational institution
 */
export const isEducationalEmail = (email: string): boolean => {
  const emailLower = email.toLowerCase();
  return APPROVED_TEACHER_DOMAINS.some(domain => emailLower.endsWith(domain));
};

/**
 * Check if user has teacher role
 */
export const isTeacher = (role: UserRole | undefined): boolean => {
  return role === 'teacher';
};

/**
 * Check if user has pending teacher status
 */
export const isPendingTeacher = (role: UserRole | undefined): boolean => {
  return role === 'pending_teacher';
};